import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "@/platform/components/AuthShell";
import { Button, TextField } from "@/platform/components/primitives";
import PasswordField from "@/platform/components/PasswordField";
import OtpInput from "@/platform/components/OtpInput";
import { api } from "@/platform/api";

export default function Forgot() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/api/auth/forgot/request", { email: email.trim() });
      if (res.dev_code) setDevCode(res.dev_code);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (code.length !== 6) { setError("Enter the 6-digit code."); return; }
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError("Password must be 8+ characters with a letter and a number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/api/auth/forgot/reset", { email: email.trim(), code, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <AuthShell footer={<span>Remembered it? <Link to="/login" className="font-semibold text-[#F4F5F8] hover:underline">Back to sign in</Link></span>}>
      <div className="animate-in fade-in duration-500">
        <h1 className="text-[26px] font-semibold tracking-tight text-[#F4F5F8]">Reset your password</h1>
        {step === 1 ? (
          <>
            <p className="mt-2 text-[15px] text-[#98A0B0]">Enter your email and we’ll send a reset code.</p>
            <form onSubmit={request} className="mt-8 space-y-4">
              <TextField id="email" label="Email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} error={error} autoFocus />
              <Button type="submit" loading={loading}>Send reset code</Button>
            </form>
          </>
        ) : (
          <>
            <p className="mt-2 text-[15px] text-[#98A0B0]">Enter the code sent to <span className="font-semibold text-[#F4F5F8]">{email}</span> and choose a new password.</p>
            {devCode && (
              <div className="mt-5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3.5 py-2.5 text-[12.5px] text-amber-200">
                <span className="font-semibold">Dev mode:</span> code is <span className="font-mono font-bold tracking-widest">{devCode}</span>
              </div>
            )}
            <form onSubmit={reset} className="mt-6 space-y-5">
              <OtpInput value={code} onChange={setCode} disabled={loading} error={!!error} />
              <PasswordField label="New password" value={password} onChange={setPassword} />
              {error && <p className="text-[12.5px] text-red-500">{error}</p>}
              <Button type="submit" loading={loading}>Reset password</Button>
            </form>
          </>
        )}
      </div>
    </AuthShell>
  );
}
